/**
 * Which rail section a row belongs to. The Favorites group is a shortcut
 * projection of items that also live in a canonical section, so the same
 * resource id renders twice. Tracking which section the user clicked lets the
 * selected highlight follow that row instead of lighting up both copies.
 */
export type RailSelectionSource = "favorites" | "canonical" | "context";

/** Selectable resource kinds that participate in rail multi-select. */
export type RailSelectionKind = "canvas" | "chat" | "file" | "skill";

/**
 * Visible-row selection key. Section is part of the key so a Favorites copy
 * and its canonical twin don't both light up from one click; bulk actions
 * strip the section and dedupe by kind+id.
 */
export function railSelectionKey(
  section: RailSelectionSource,
  kind: RailSelectionKind,
  id: string,
): string {
  return `${section}:${kind}:${id}`;
}

export function parseRailSelectionKey(key: string): {
  section: RailSelectionSource;
  kind: RailSelectionKind;
  id: string;
} | null {
  const first = key.indexOf(":");
  const second = key.indexOf(":", first + 1);
  if (first <= 0 || second <= first) return null;
  const section = key.slice(0, first) as RailSelectionSource;
  const kind = key.slice(first + 1, second) as RailSelectionKind;
  const id = key.slice(second + 1);
  if (!id) return null;
  if (
    section !== "favorites" &&
    section !== "canonical" &&
    section !== "context"
  ) {
    return null;
  }
  if (
    kind !== "canvas" &&
    kind !== "chat" &&
    kind !== "file" &&
    kind !== "skill"
  ) {
    return null;
  }
  return { section, kind, id };
}

/** Dedupe selected row keys to unique kind+id pairs for bulk actions. */
export function dedupeRailSelectionResources(
  keys: Iterable<string>,
): Array<{ kind: RailSelectionKind; id: string }> {
  const seen = new Set<string>();
  const out: Array<{ kind: RailSelectionKind; id: string }> = [];
  for (const key of keys) {
    const parsed = parseRailSelectionKey(key);
    if (!parsed) continue;
    const resourceKey = `${parsed.kind}:${parsed.id}`;
    if (seen.has(resourceKey)) continue;
    seen.add(resourceKey);
    out.push({ kind: parsed.kind, id: parsed.id });
  }
  return out;
}
